import { useCallback, useMemo, type CSSProperties, type ReactElement } from "react";
import { ArrowLeft } from "lucide-react";
import { color, space } from "@tokens/index";
import { ContentSwap, IconButton, SkeletonRows, Text, useViewport } from "../../primitives";
import { buildQueueNameResolver, type QueueNameResolver } from "../workflowRecipients";
import { staleDataStyle } from "../staleData";
import { useNow } from "../useNow";
import type { WorkItem } from "../../../experience-shell/workflows";
import { BoardRail } from "./Rail";
import { ComposerFooter, MessageComposer, MessageStream } from "./Messaging";
import { ConversationCloseBar } from "./Threads";
import { NotePanel, NotesListPanel } from "./Notes";
import { TaskPanel } from "./TaskPanel";
import { hairline } from "./expandMotion";
import { sceneMeasures } from "./measures";
import type { RailEntry, RailGroup, ThreadSectionProps, WorkflowBoardProps } from "./types";

export type {
  AddTaskDraft,
  DestinationQueue,
  RailSelection,
  StartThreadDraft,
  TaskTypeOption,
  WorkflowBoardProps,
} from "./types";

/** The panel column that both the skeleton and the loaded panel fill. */
const panelColumn: CSSProperties = {
  flex: 1,
  minHeight: 0,
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
};

/**
 * Master-detail body for one workflow: the work-item rail on the left and
 * the selected item on the right. The scene header lives in `SceneHeader`
 * and is not part of this component. On `compact` the two regions show one
 * at a time: the rail until a selection exists, then the panel with a back
 * control. Regions separate by tone (rail on `surface`, panel on `canvas`).
 */
export function WorkflowBoard(props: WorkflowBoardProps): ReactElement {
  const {
    workflow,
    isLoading = false,
    isStale = false,
    taskPresenceSlot,
    currentUserId,
    approverQueueIds,
    queueTree,
    originatingQueueId,
    taskTypeOptions,
    destinationQueues,
    requireExplicitDestination,
    initialCreatorOpen,
    onCreatorClosed,
    selectedItemId,
    onSelectItem,
    laneOpen,
    onLaneOpenChange,
    threads,
    isLoadingThreads = false,
    isThreadsStale = false,
    selectedThreadId,
    onSelectThread,
    activeThread,
    isLoadingActiveThread = false,
    isActiveThreadStale = false,
    onSendGeneralMessage,
    onSendThreadMessage,
    onStartThread,
    onAddTask,
    onAddNote,
    onCompleteWorkItem,
    onApproveWorkItem,
    onRejectWorkItem,
    onSnoozeWorkItem,
    onNavigateToGalileoTask,
    canNavigateToGalileoTask,
    onReassignWorkItemQueue,
    onPinNote,
    onEditNote,
    onApproveThreadAnswer,
    onRejectThreadAnswer,
    onCloseConversation,
    onCloseThread,
    isSending = false,
    isStartingThread = false,
    isCompletingId = null,
    isApprovingId = null,
    isRejectingId = null,
    isSnoozingId = null,
    isPinningId = null,
    isEditingNoteId = null,
    isReassigningQueueId = null,
    isApprovingThreadAnswerId = null,
    isRejectingThreadAnswerId = null,
    isClosingConversation = false,
    isClosingThread = false,
  } = props;

  const { isCompact, isAtLeast } = useViewport();
  const expanded = isAtLeast("expanded");
  const wide = isAtLeast("wide");

  const handleSelectItem = useCallback(
    (id: string | null) => {
      onSelectItem(id);
      onLaneOpenChange(id === null);
    },
    [onSelectItem, onLaneOpenChange],
  );
  const handleBackToRail = useCallback(() => {
    onSelectItem(null);
    onLaneOpenChange(false);
  }, [onSelectItem, onLaneOpenChange]);

  const panelVisible = !isCompact || selectedItemId !== null || laneOpen;
  const railVisible = !isCompact || !panelVisible;
  const onBackToRail = isCompact ? handleBackToRail : undefined;
  const railWidth = isCompact
    ? undefined
    : expanded
      ? sceneMeasures.railWidth
      : sceneMeasures.railWidthMedium;

  // `ad-hoc` and `child-thread` are conversations: they carry a chat lane.
  // Every other kind carries a notes lane instead.
  const isConversation = workflow.kind === "ad-hoc" || workflow.kind === "child-thread";

  // One "as of" instant shared by the rail and the panels. It advances every
  // 30s (Pending -> Open crossings, relative labels) and is stable between
  // ticks, so `now`-dependent memos and rows do not recompute per render.
  const now = useNow(30_000);

  const resolveQueueName = useMemo<QueueNameResolver>(
    () => buildQueueNameResolver(queueTree),
    [queueTree],
  );

  const { generalMessages, tasks, notes } = useMemo(() => {
    const messages: WorkItem[] = [];
    const taskItems: WorkItem[] = [];
    const noteItems: WorkItem[] = [];
    for (const item of workflow.workItems) {
      if (item.type === "task") taskItems.push(item);
      else if (item.type === "note") noteItems.push(item);
      else messages.push(item);
    }
    return { generalMessages: messages, tasks: taskItems, notes: noteItems };
  }, [workflow.workItems]);

  // Rail groups: Tasks, then the lane. Conversations list their chat lane
  // and every note under "Notes"; note-first kinds keep unpinned notes inside
  // the notes lane and surface only the pinned ones beside it.
  const railGroups = useMemo<RailGroup[]>(() => {
    const groups: RailGroup[] = [];
    const taskEntries: RailEntry[] = tasks.map((t) => ({
      key: t.id,
      kind: "task",
      id: t.id,
      item: t,
    }));
    if (taskEntries.length > 0) groups.push({ key: "tasks", label: "Tasks", entries: taskEntries });

    const pinned: RailEntry[] = notes
      .filter((n) => n.pinned)
      .map((n) => ({ key: n.id, kind: "note", id: n.id, item: n }));
    if (isConversation) {
      groups.push({
        key: "chat",
        label: "Chat",
        entries: [{ key: "general", kind: "general", id: null, item: null }],
      });
      const unpinned: RailEntry[] = notes
        .filter((n) => !n.pinned)
        .map((n) => ({ key: n.id, kind: "note", id: n.id, item: n }));
      const noteEntries = [...pinned, ...unpinned];
      if (noteEntries.length > 0)
        groups.push({ key: "notes", label: "Notes", entries: noteEntries });
    } else {
      groups.push({
        key: "notes",
        label: "Notes",
        entries: [{ key: "notes", kind: "notes", id: null, item: null }, ...pinned],
      });
    }
    return groups;
  }, [tasks, notes, isConversation]);

  const rootNoteCount = useMemo(
    () => notes.filter((n) => n.parentWorkItemId === null).length,
    [notes],
  );

  const selectedTask = useMemo(
    () => (selectedItemId === null ? null : (tasks.find((t) => t.id === selectedItemId) ?? null)),
    [selectedItemId, tasks],
  );
  const selectedNote = useMemo(
    () => (selectedItemId === null ? null : (notes.find((n) => n.id === selectedItemId) ?? null)),
    [selectedItemId, notes],
  );

  const threadSection: ThreadSectionProps = {
    threads,
    isLoadingThreads,
    isThreadsStale,
    selectedThreadId,
    onSelectThread,
    activeThread,
    isLoadingActiveThread,
    isActiveThreadStale,
    now,
    currentUserId,
    resolveQueueName,
    queueTree,
    originatingQueueId,
    approverQueueIds,
    isSending,
    isStartingThread,
    isApprovingThreadAnswerId,
    isRejectingThreadAnswerId,
    onSendThreadMessage,
    onStartThread,
    onApproveThreadAnswer,
    onRejectThreadAnswer,
    onCloseThread,
    isClosingThread,
  };

  return (
    <div
      data-testid="workflow-board"
      data-stale={isStale || undefined}
      style={{
        flex: 1,
        minHeight: 0,
        minWidth: 0,
        display: "flex",
        background: color.bg.canvas,
        ...staleDataStyle(isStale),
      }}
    >
      {railVisible && (
        <BoardRail
          groups={railGroups}
          selectedItemId={selectedItemId}
          onSelectItem={handleSelectItem}
          generalMessageCount={generalMessages.length}
          rootNoteCount={rootNoteCount}
          now={now}
          taskTypeOptions={taskTypeOptions}
          destinationQueues={destinationQueues ?? []}
          requireExplicitDestination={requireExplicitDestination === true}
          initialCreatorOpen={initialCreatorOpen === true}
          {...(onCreatorClosed !== undefined ? { onCreatorClosed } : {})}
          onAddTask={onAddTask}
          onAddNote={onAddNote}
          width={railWidth}
          loading={isLoading}
        />
      )}

      {panelVisible && (
        <div
          data-testid="workflow-board-panel"
          style={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
          }}
        >
          {/* On `wide` the content column caps at a readable measure; the region stays full-bleed. */}
          <div
            style={{
              flex: 1,
              minHeight: 0,
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              width: "100%",
              maxWidth: wide ? sceneMeasures.contentMaxWidth : undefined,
              alignSelf: "center",
            }}
          >
            <ContentSwap
              ready={!isLoading}
              style={panelColumn}
              layerStyle={panelColumn}
              skeleton={
                <div style={{ padding: space[4] }}>
                  <SkeletonRows label="Loading workflow" rows={5} />
                </div>
              }
            >
              {selectedNote !== null ? (
                <NotePanel
                  note={selectedNote}
                  isPinning={isPinningId === selectedNote.id}
                  onPinNote={onPinNote}
                  isEditingNote={isEditingNoteId === selectedNote.id}
                  onEditNote={onEditNote}
                  onBackToRail={onBackToRail}
                  actionsInline={expanded}
                  threadSection={threadSection}
                />
              ) : selectedTask !== null ? (
                <TaskPanel
                  task={selectedTask}
                  now={now}
                  notes={notes}
                  onAddNote={onAddNote}
                  isAddingNote={isSending}
                  onPinNote={onPinNote}
                  isPinningId={isPinningId}
                  queueTree={queueTree}
                  canApprove={
                    selectedTask.requestedByQueueId != null &&
                    approverQueueIds?.has(selectedTask.requestedByQueueId) === true
                  }
                  isCompleting={isCompletingId === selectedTask.id}
                  isApproving={isApprovingId === selectedTask.id}
                  isRejecting={isRejectingId === selectedTask.id}
                  isSnoozing={isSnoozingId === selectedTask.id}
                  onComplete={onCompleteWorkItem}
                  onApprove={onApproveWorkItem}
                  onReject={onRejectWorkItem}
                  onSnooze={onSnoozeWorkItem}
                  onReassignQueue={onReassignWorkItemQueue}
                  isReassigningQueue={isReassigningQueueId === selectedTask.id}
                  onNavigateToGalileo={
                    onNavigateToGalileoTask !== undefined &&
                    (canNavigateToGalileoTask === undefined ||
                      canNavigateToGalileoTask(selectedTask))
                      ? () => onNavigateToGalileoTask(selectedTask)
                      : undefined
                  }
                  presenceSlot={taskPresenceSlot ?? null}
                  actionsInline={expanded}
                  onBackToRail={onBackToRail}
                  threadSection={threadSection}
                />
              ) : isConversation ? (
                <>
                  {onBackToRail !== undefined && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: space[2],
                        padding: `${space[1]} ${space[2]}`,
                        borderBottom: hairline,
                        flexShrink: 0,
                      }}
                    >
                      <IconButton
                        icon={ArrowLeft}
                        variant="ghost"
                        size="md"
                        aria-label="Back to work items"
                        onClick={onBackToRail}
                      />
                      <Text role="body" size="sm" weight="medium" truncate>
                        Chat
                      </Text>
                    </div>
                  )}
                  {onCloseConversation !== undefined && generalMessages.length > 0 && (
                    <ConversationCloseBar
                      onClose={onCloseConversation}
                      isClosing={isClosingConversation}
                      isClosed={!generalMessages.some((m) => m.completedAt === null)}
                      openLabel="Close conversation"
                      closedLabel="Conversation closed"
                    />
                  )}
                  <MessageStream
                    items={generalMessages}
                    currentUserId={currentUserId}
                    resolveQueueName={resolveQueueName}
                    emptyHint="No messages yet — start the conversation."
                  />
                  <ComposerFooter>
                    <MessageComposer
                      placeholder="Type a message…"
                      disabled={isSending}
                      onSend={onSendGeneralMessage}
                    />
                  </ComposerFooter>
                </>
              ) : (
                <NotesListPanel
                  notes={notes}
                  onSelectNote={(id) => handleSelectItem(id)}
                  onAddNote={onAddNote}
                  onBackToRail={onBackToRail}
                />
              )}
            </ContentSwap>
          </div>
        </div>
      )}
    </div>
  );
}
